import { apiClient } from "./apiService";

// Get all sports with enhanced filtering
export const getAllSports = async (filters = {}) => {
  const params = new URLSearchParams();

  // Existing filters
  if (filters.category) params.append("category", filters.category);
  if (filters.search) params.append("search", filters.search);

  // New enhanced filters
  if (filters.sport_type) params.append("sport_type", filters.sport_type);
  if (filters.difficulty_level)
    params.append("difficulty_level", filters.difficulty_level);
  if (filters.olympic_sport !== undefined)
    params.append("olympic_sport", filters.olympic_sport);
  if (filters.physical_intensity)
    params.append("physical_intensity", filters.physical_intensity);
  if (filters.season_type) params.append("season_type", filters.season_type);
  if (filters.limit) params.append("limit", filters.limit);
  if (filters.page) params.append("page", filters.page);

  // Add cache-busting parameter to ensure fresh data
  params.append("_t", Date.now());

  const response = await apiClient.get(`/sports?${params}`);
  return response.data;
};

// Get sport by ID
export const getSportById = async (id) => {
  const response = await apiClient.get(`/sports/${id}`);
  return response.data;
};

// Get sport with configuration details
export const getSportWithConfig = async (sportId) => {
  const response = await apiClient.get(`/sports/${sportId}/config`);
  return response.data;
};

// Get available positions for a sport
export const getSportPositions = async (sportId) => {
  const response = await apiClient.get(
    `/sports/${sportId}/positions`
  );
  return response.data;
};

// Get user's sports
export const getUserSports = async (token) => {
  // Note: token parameter kept for backward compatibility
  // Authorization header is automatically added by apiClient interceptor
  const response = await apiClient.get(`/sports/user/my-sports`);
  return response.data;
};

// Add sport to user
export const addSportToUser = async (token, sportId, sportData) => {
  // Note: token parameter kept for backward compatibility
  // Authorization header is automatically added by apiClient interceptor
  const response = await apiClient.post(
    `/sports/${sportId}/join`,
    sportData
  );
  return response.data;
};

// Remove sport from user
export const removeSportFromUser = async (token, sportId) => {
  // Note: token parameter kept for backward compatibility
  // Authorization header is automatically added by apiClient interceptor
  const response = await apiClient.delete(
    `/sports/${sportId}/leave`
  );
  return response.data;
};

// Get popular sports
export const getPopularSports = async (limit = 10) => {
  const response = await apiClient.get(
    `/sports/popular?limit=${limit}`
  );
  return response.data;
};

// Get sports by category
export const getSportsByCategory = async () => {
  const response = await apiClient.get(`/sports/categories`);
  return response.data;
};

// Search sports with advanced filters
export const searchSports = async (filters) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  const response = await apiClient.get(`/sports/search?${params}`);
  return response.data;
};

// Get recommended sports for user
export const getRecommendedSports = async (token) => {
  const response = await apiClient.get(`/sports/recommendations/user`);
  return response.data;
};

// ✅ NEW: RBAC-Enhanced Sports CRUD Operations

// Create new sport (requires sports.write permission)
export const createSport = async (token, sportData) => {
  const response = await apiClient.post(`/sports`, sportData);
  return response.data;
};

// Update sport (requires sports.write permission)
export const updateSport = async (token, sportId, sportData) => {
  const response = await apiClient.put(`/sports/${sportId}`, sportData);
  return response.data;
};

// Delete sport (requires sports.delete permission)
export const deleteSport = async (token, sportId) => {
  const response = await apiClient.delete(`/sports/${sportId}`);
  return response.data;
};

// Toggle sport status (requires sports.admin permission)
export const toggleSportStatus = async (token, sportId) => {
  const response = await apiClient.patch(`/sports/${sportId}/status`, {});
  return response.data;
};

// Get all sports including inactive (admin only — requires sports.admin permission)
export const getAllSportsAdmin = async () => {
  const response = await apiClient.get(`/sports/admin/all`);
  return response.data;
};

// Update sport popularity (requires sports.admin permission)
export const updateSportPopularity = async (token, sportId, action) => {
  const response = await apiClient.patch(`/sports/${sportId}/popularity`, { action });
  return response.data;
};

// Get sports suitable for specific participant count
export const getSportsForParticipants = async (participantCount) => {
  const response = await apiClient.get(
    `/sports/by-participants?participantCount=${participantCount}`
  );
  return response.data;
};

// Get sport statistics
export const getSportStatistics = async (sportId) => {
  const response = await apiClient.get(`/sports/${sportId}/statistics`);
  return response.data;
};

// Validate game configuration
export const validateGameConfig = async (token, sportId, gameConfig) => {
  const response = await apiClient.post(
    `/sports/${sportId}/validate-config`,
    gameConfig
  );
  return response.data;
};

// Get merged game configuration
export const getMergedGameConfig = async (token, sportId, customConfig) => {
  const response = await apiClient.post(
    `/sports/${sportId}/merge-config`,
    customConfig
  );
  return response.data;
};

// Enhanced Sports API Methods

// Get sport type statistics
export const getSportTypeStats = async () => {
  const response = await apiClient.get(`/sports/stats/sport-types`);
  return response.data;
};

// Get category statistics
export const getCategoryStats = async () => {
  const response = await apiClient.get(`/sports/stats/categories`);
  return response.data;
};

// Get Olympic sports statistics
export const getOlympicSportsStats = async () => {
  const response = await apiClient.get(`/sports/stats/olympic`);
  return response.data;
};

// Get difficulty level statistics
export const getDifficultyLevelStats = async () => {
  const response = await apiClient.get(`/sports/stats/difficulty`);
  return response.data;
};

// Get all categories
export const getCategories = async () => {
  const response = await apiClient.get(`/sports/categories`);
  return response.data;
};

// Get comprehensive statistics (requires auth)
export const getComprehensiveStats = async (token) => {
  const response = await apiClient.get(`/sports/stats/comprehensive`);
  return response.data;
};

// Note: updateSportPopularity is already defined above in RBAC section
